import React, { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  closeCaptureModal,
  getCapturedContext,
  getIsCaptureModalOpen,
  getIsSubmittingFeedback,
  submitDevFeedback,
} from "../../features/devFeedback/devFeedbackSlice";
import { DevFeedbackSeverity } from "../../types/devFeedback";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { t } from "../../locales";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

const modalSelectStyles: any = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof filterSelectStyles.control === "function"
      ? (filterSelectStyles.control as any)(base, state)
      : base),
    borderRadius: "0.5rem",
    fontSize: "1rem",
  }),
};

type FormInput = {
  message: string;
  severity: DevFeedbackSeverity;
};

const defaultValues: FormInput = { message: "", severity: "suggestion" };

const severityOptions: { value: DevFeedbackSeverity; label: string }[] = [
  { value: "bug", label: t.devFeedback.severityBug },
  { value: "ui", label: t.devFeedback.severityUi },
  { value: "suggestion", label: t.devFeedback.severitySuggestion },
  { value: "other", label: t.devFeedback.severityOther },
];

type Props = {
  widgetAttr: string;
};

const FeedbackCaptureDialog: React.FC<Props> = ({ widgetAttr }) => {
  const dispatch = useAppDispatch();
  const open = useAppSelector(getIsCaptureModalOpen);
  const context = useAppSelector(getCapturedContext);
  const submitting = useAppSelector(getIsSubmittingFeedback);

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<FormInput>({ defaultValues });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const handleClose = () => {
    dispatch(closeCaptureModal());
  };

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (!context) return;
    const action: any = await dispatch(
      submitDevFeedback({ ...context, message: data.message, severity: data.severity })
    );
    const response = action.payload;
    if (response?.data?.status === "success") {
      toast.success(t.devFeedback.submitSuccess, { ...(toastStyles as any) });
      dispatch(closeCaptureModal());
    } else {
      toast.error(response?.data?.message || t.devFeedback.submitError, {
        ...(toastStyles as any),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent {...{ [widgetAttr]: "" }} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {t.devFeedback.captureTitle}
          </DialogTitle>
          <DialogDescription>{t.devFeedback.captureDescription}</DialogDescription>
        </DialogHeader>

        {context && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 mb-1 space-y-1 text-xs text-gray-600">
            <div>
              <span className="font-semibold text-gray-500">{t.devFeedback.metaPage}:</span>{" "}
              {context.page_path}
            </div>
            {context.component_label && (
              <div>
                <span className="font-semibold text-gray-500">{t.devFeedback.metaComponent}:</span>{" "}
                {context.component_label}
              </div>
            )}
            {context.element_tag && (
              <div>
                <span className="font-semibold text-gray-500">{t.devFeedback.metaElement}:</span>{" "}
                &lt;{context.element_tag}&gt;
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="feedback-severity" className="text-sm font-semibold text-[#113F67]">
              {t.devFeedback.severityLabel}
            </Label>
            <Controller
              name="severity"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  inputId="feedback-severity"
                  styles={modalSelectStyles}
                  options={severityOptions}
                  value={severityOptions.find((o) => o.value === field.value) || null}
                  onChange={(opt: any) => field.onChange(opt?.value || "suggestion")}
                  isClearable={false}
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-message" className="text-sm font-semibold text-[#113F67]">
              {t.devFeedback.messageLabel}
            </Label>
            <Textarea
              id="feedback-message"
              rows={4}
              placeholder={t.devFeedback.messagePlaceholder}
              {...register("message", { required: true })}
            />
            {errors.message && (
              <span className="text-red-500 text-sm">{t.devFeedback.messageRequired}</span>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              {t.devFeedback.cancelButton}
            </Button>
            <Button
              type="submit"
              className="bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              disabled={submitting}
            >
              {submitting ? t.devFeedback.submitting : t.devFeedback.submitButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackCaptureDialog;
